import { CloseOutlined } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { useAlert } from "../../../../hooks/useAlert";
import { useUpdateTaskStatusMutation } from "../../../../redux-toolkit/features/books/bookApiSlice";
import "./TaskAccordian.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  right: "50%",
  maxHeight: "50vh",
  overflowY: "auto",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: "40px 20px",
};

const formatSubmissionTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getSubmissionTimestamp = (task) => {
  if (!task) return null;
  if (task.status === "PENDING") {
    return task.updatedAt || task.createdAt;
  }
  return task.createdAt || task.updatedAt;
};

const getReviewedTimeDisplay = (task) => {
  if (!task?.reviewedAt) return null;
  const time = formatSubmissionTime(task.reviewedAt);
  if (!time) return null;

  if (task.status === "APPROVED") {
    return { label: "Approved on", time };
  }
  if (task.status === "REJECTED") {
    return { label: "Rejected on", time };
  }
  return null;
};

export const TaskAccordian = ({ taskData, readOnly = false, hideBookName = false }) => {
  const toBrowserUrl = (url = "") => {
    const trimmed = (url || "").trim();
    if (!trimmed) return "";
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const [status, setStatus] = useState(false);
  const [openImageModel, setOpenImageModel] = useState(false);
  const [imageDisplaySize, setImageDisplaySize] = useState({
    width: null,
    height: null,
  });
  const [openModel, setOpenModel] = useState("");
  const [openReasons, setOpenReasons] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const { displayAlert } = useAlert();
  const rejectionHistory = Array.isArray(taskData?.rejectionReasons)
    ? [...taskData.rejectionReasons].reverse()
    : [];
  const isPendingTask = taskData?.status === "PENDING";
  const submissionTime = formatSubmissionTime(getSubmissionTimestamp(taskData));
  const reviewedTimeDisplay = getReviewedTimeDisplay(taskData);

  const handlePreviewImageLoad = (e) => {
    const img = e.target;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    const maxW = window.innerWidth * 0.7;
    const maxH = window.innerHeight * 0.7;
    const scale = Math.min(maxW / naturalWidth, maxH / naturalHeight, 1);
    const width = Math.round(naturalWidth * scale);
    const height = Math.round(naturalHeight * scale);
    setImageDisplaySize({ width, height });
  };

  const handlePreviewClose = () => {
    setOpenImageModel(false);
    setImageDisplaySize({ width: null, height: null });
  };

  const closeConfirm = () => {
    setOpenModel("");
    setRejectionReason("");
  };

  const trimmedRejectionReason = rejectionReason.trim();
  const rejectionReasonTooShort =
    trimmedRejectionReason.length > 0 && trimmedRejectionReason.length < 5;
  const rejectionReasonTooLong = trimmedRejectionReason.length > 200;
  const rejectionReasonInvalid = rejectionReasonTooShort || rejectionReasonTooLong;

  const statusHandler = async (nextStatus) => {
    if (nextStatus === "reject" && rejectionReasonInvalid) return;

    try {
      await updateTaskStatus({
        status: nextStatus,
        taskCompletionId: taskData?._id,
        ...(nextStatus === "reject"
          ? { rejectionReason: trimmedRejectionReason }
          : {}),
      }).unwrap();
      closeConfirm();
      displayAlert({
        open: true,
        message:
          nextStatus === "approve"
            ? `The task has been approved`
            : "The task has been rejected",
        type: "success",
        timeout: "1500",
      });
    } catch (error) {
      displayAlert({
        open: true,
        message: error?.data?.message || "Something went wrong",
        type: "error",
        timeout: "2000",
      });
    }
  };

  // useEffect(() => {
  //   if (openImageModel) {
  //     const div = document.createElement("div");
  //     div.style =
  //       "position: fixed; inset: 0; width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; background-color: #00000088; z-index: 100000";
  //     div.id = "attachment_modal";
  //     const img = document.createElement("img");
  //     img.style = "object-fit: contain; height: 90%;";
  //     img.src = taskData?.attachments?.filename;
  //     img.alt = "attachment";
  //     div.appendChild(img);
  //     div.addEventListener("click", (e) => {
  //       setOpenImageModel(false);
  //     });
  //     img.addEventListener("click", (e) => {
  //       e.stopPropagation();
  //     });
  //     document.body.appendChild(div);
  //   } else {
  //     let modal = document.getElementById("attachment_modal");
  //     modal && modal.remove();
  //   }
  // }, [openImageModel]);

  return (
    <>
      {/* Attachment preview dialog (same pattern as TimeTrials tab) */}
      <Dialog
        open={openImageModel}
        onClose={handlePreviewClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: "70vw",
            maxHeight: "70vh",
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "& .MuiDialogContent-root": { padding: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "4px 8px 4px 16px",
            flexShrink: 0,
            margin: 0,
          }}
        >
          <IconButton
            onClick={handlePreviewClose}
            size="small"
            sx={{ color: "#666" }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            margin: 0,
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "calc(70vh - 52px)",
            "&.MuiDialogContent-root": { padding: 0 },
          }}
        >
          {openImageModel && taskData?.attachments?.filename && (
            <img
              src={taskData.attachments.filename}
              alt="Attachment preview"
              onLoad={handlePreviewImageLoad}
              style={{
                maxWidth: "70vw",
                maxHeight: "calc(70vh - 52px)",
                width: imageDisplaySize.width
                  ? `${imageDisplaySize.width}px`
                  : "auto",
                height: imageDisplaySize.height
                  ? `${imageDisplaySize.height}px`
                  : "auto",
                objectFit: "contain",
                display: "block",
                verticalAlign: "middle",
                marginBottom: "72px",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Modal
        open={!!openModel}
        onClose={closeConfirm}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="deletemodal">
            <h3 className="ftwt__r deletemodal__title">
              Are you sure you want to {openModel} the task ?
            </h3>
            {openModel === "reject" ? (
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Reason"
                placeholder="Optional. 5–200 characters if you add one."
                value={rejectionReason}
                onChange={(event) =>
                  setRejectionReason(event.target.value.slice(0, 200))
                }
                inputProps={{ maxLength: 200 }}
                error={rejectionReasonInvalid}
                helperText={
                  rejectionReasonTooShort
                    ? "Reason must be at least 5 characters"
                    : rejectionReasonTooLong
                      ? "Reason must be at most 200 characters"
                      : "Optional. 5–200 characters, or leave blank to record NA."
                }
                sx={{ mt: 2 }}
              />
            ) : null}
            <div className="deletemodal__action">
              <Button
                onClick={closeConfirm}
                color="error"
                variant="outlined"
              >
                No
              </Button>
              <Button
                onClick={() => {
                  statusHandler(openModel);
                }}
                color="success"
                variant="outlined"
                disabled={openModel === "reject" && rejectionReasonInvalid}
              >
                Yes
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
      <div className={status ? "TaskAccActive" : "TaskAcc"}>
        <div className="TaskAcc__head">
          <div className="TaskAcc__headContent">
            <h4>
              {!hideBookName && taskData?.bookDetails?.name ? (
                <>
                  {taskData.bookDetails.name} &#62;{" "}
                </>
              ) : null}
              Level {taskData?.levelNumber ?? "-"}
              {taskData?.bookType !== "ORG" && taskData?.levelName
                ? ` (${taskData.levelName})`
                : ""}{" "}
              &#62; Task {taskData?.taskNumber ?? "-"}:{" "}
              <span className="ftwt_r">{taskData?.task?.description}</span>
            </h4>
            {submissionTime ? (
              <p className="TaskAcc__submissionTime">
                Submitted on {submissionTime}
              </p>
            ) : null}
            {reviewedTimeDisplay ? (
              <p className="TaskAcc__submissionTime">
                {reviewedTimeDisplay.label} {reviewedTimeDisplay.time}
              </p>
            ) : null}
          </div>
          {!status ? (
            <button
              onClick={() => setStatus(true)}
              className="contained__lgbtn"
            >
              View
            </button>
          ) : (
            <IconButton onClick={() => setStatus(false)}>
              <CloseOutlined fontSize="small" />
            </IconButton>
          )}
        </div>
        {status && (
          <div className="TaskAcc__detail">
            <div>
              {taskData?.attachments?.url ? (
                <div className="TaskAcc__url">
                  <h4>URL</h4>
                  <a
                    href={toBrowserUrl(taskData?.attachments?.url)}
                    target="__blank"
                    rel="noopener noreferrer"
                    className="ftwt__r"
                  >
                    {taskData?.attachments?.url}{" "}
                  </a>
                </div>
              ) : null}
            </div>

            {taskData?.comments && taskData?.comments !== "undefined" ? (
              <>
                <h4>Additional Note</h4>
                <span>{taskData?.comments}</span>
              </>
            ) : null}
            {taskData?.attachments?.filename && (
              <>
                <h4>Attachments:</h4>
                <img
                  src={taskData?.attachments?.filename}
                  height={100}
                  width={100}
                  alt="Attachment"
                  onClick={() => setOpenImageModel(true)}
                  style={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    objectFit: "contain",
                  }}
                  title="Click to preview full size"
                />
              </>
            )}
            {rejectionHistory.length ? (
              <Button
                variant="text"
                onClick={() => setOpenReasons(true)}
                sx={{ alignSelf: "flex-start", px: 0 }}
              >
                Rejection reasons ({rejectionHistory.length})
              </Button>
            ) : null}
            <Dialog
              open={openReasons}
              onClose={() => setOpenReasons(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                Rejection reasons
                <IconButton
                  onClick={() => setOpenReasons(false)}
                  size="small"
                  aria-label="Close rejection reasons"
                >
                  <CloseOutlined />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <div className="TaskAcc__reasonList">
                  {rejectionHistory.map((entry, index) => {
                    const time = formatSubmissionTime(entry?.rejectedAt);
                    const reason = entry?.reason?.trim() || "NA";
                    return (
                      <div
                        className="TaskAcc__reasonItem"
                        key={`${entry?.rejectedAt || "na"}-${index}`}
                      >
                        {time ? (
                          <p className="TaskAcc__reasonTime">{time}</p>
                        ) : null}
                        <p className="TaskAcc__reasonText">{reason}</p>
                      </div>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>
            <div className="seprator"></div>
            {isPendingTask && !readOnly ? (
              <div className="TaskAcc__actions">
                <Button
                  onClick={() => setOpenModel("reject")}
                  sx={{ width: "100px" }}
                  variant="outlined"
                  color="error"
                >
                  Reject
                </Button>
                <Button
                  onClick={() => setOpenModel("approve")}
                  sx={{ width: "100px" }}
                  variant="outlined"
                  color="success"
                >
                  Accept
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};
